import { useTranslation } from 'react-i18next'

import {
  ReferralLinkUrlField,
  TVariant,
  VARIANT,
} from '@pages/IntroducingBroker/Dashboard/ReferralLink/ReferralLinkUrlField'
import { getLinkStyles, isCustomUrl } from '@pages/IntroducingBroker/LinkHub/Links/helpers'

import { SpacingType } from '@ui/design-system/spacing'

import { useWindowResize } from '@utils/domUtils'

import { IntroducingBrokerReferralTypes } from '@model/IntroducingBrokerDetailsDto'
import { NameDto } from '@model/NameDto'

import { Box } from '@shared/components/Box'

interface ReferralLinkUrlFieldsProps {
  activeReferralType: IntroducingBrokerReferralTypes
  mainReferralLink?: string | null
  mtReferralLink?: string | null
  shortenedMainReferralLink?: string
  shortenedMTReferralLink?: string
  isShortMainLinkLoading: boolean
  isShortMTLinkLoading: boolean
  isShorteningAvailable: { canShortenMainIB: boolean; canShortenMTIB: boolean }
  handleIBLinkShortening: (code: string) => Promise<void>
  isCustomLpUrlSelected: boolean
  landingPageUrl?: NameDto<string>
  resolvedLandingPageUrl?: string | null
  ibCode?: string
  mtCode?: string
  mtLink?: string | null
  variant?: TVariant
  mainLinkMt?: SpacingType
}

export const ReferralLinkUrlFields = ({
  activeReferralType,
  mainReferralLink,
  mtReferralLink,
  shortenedMainReferralLink,
  shortenedMTReferralLink,
  isShortMainLinkLoading,
  isShortMTLinkLoading,
  isShorteningAvailable,
  handleIBLinkShortening,
  isCustomLpUrlSelected,
  landingPageUrl,
  resolvedLandingPageUrl,
  ibCode,
  mtCode,
  mtLink,
  variant = VARIANT.DEFAULT,
  mainLinkMt,
}: ReferralLinkUrlFieldsProps) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const linkStyles = getLinkStyles(isMobile)

  const sharedProps = {
    ...(variant !== VARIANT.NO_LABEL && {
      placeholder: t('IB.LinkHubPage.URL output'),
      label: t('IB.LinkHubPage.Output'),
    }),
    variant,
    onShortenIBLink: handleIBLinkShortening,
  }

  return (
    <>
      {(mainReferralLink || shortenedMainReferralLink) &&
        activeReferralType === IntroducingBrokerReferralTypes.Default && (
          <Box mt={mainLinkMt ?? linkStyles.mt}>
            <ReferralLinkUrlField
              {...sharedProps}
              referralLink={mainReferralLink}
              ibCode={ibCode}
              shortenedReferralLink={shortenedMainReferralLink}
              isShortLinkLoading={isShortMainLinkLoading}
              isShorteningAvailable={isShorteningAvailable.canShortenMainIB}
              isCustomUrl={isCustomUrl({
                isCustomLpUrlSelected,
                isLinkShortened: !!shortenedMainReferralLink,
                isLinkExist: !!mainReferralLink,
                id: landingPageUrl?.id,
              })}
              landingPageUrl={resolvedLandingPageUrl}
              isLinkHub
            />
          </Box>
        )}
      {(mtReferralLink || shortenedMTReferralLink) &&
        activeReferralType === IntroducingBrokerReferralTypes.MultiTier && (
          <Box mt={linkStyles.mt}>
            <ReferralLinkUrlField
              {...sharedProps}
              referralLink={mtReferralLink}
              ibCode={mtCode}
              shortenedReferralLink={shortenedMTReferralLink}
              isShortLinkLoading={isShortMTLinkLoading}
              isShorteningAvailable={isShorteningAvailable.canShortenMTIB}
              isCustomUrl={isCustomUrl({
                isCustomLpUrlSelected,
                isLinkShortened: !!shortenedMTReferralLink,
                isLinkExist: !!mtLink,
                id: landingPageUrl?.id,
              })}
              landingPageUrl={resolvedLandingPageUrl}
              isLinkHub
            />
          </Box>
        )}
    </>
  )
}
